import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { publicAPI } from "@/services/api";
import { getErrorMessage } from "@/utils/errorHelpers";
import ContactInfoBlock from "@/components/app/ContactInfoBlock";
import { ArrowLeft, ShieldAlert, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function AccountDeletionPage() {
  const [form, setForm] = useState({ email: "", full_name: "", reason: "" });
  const [submitting, setSubmitting] = useState(false);
  const [requestId, setRequestId] = useState("");

  const update = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const submit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    try {
      const response = await publicAPI.requestAccountDeletion(form);
      setRequestId(response.data?.request_id || "");
      toast.success(response.data?.message || "Demande envoyee");
    } catch (error) {
      toast.error(getErrorMessage(error, "Demande impossible"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#FCFBF8] px-4 py-6 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-3xl space-y-6">
        <Button asChild variant="outline" className="gap-2">
          <Link to="/">
            <ArrowLeft size={16} /> Accueil
          </Link>
        </Button>

        <Card className="border-[#E6E4E0] shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-2xl text-[#2A4B46] font-['Outfit']">
              <Trash2 size={22} /> Demande de suppression de compte
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm leading-6 text-amber-900">
              <p className="flex items-center gap-2 font-semibold">
                <ShieldAlert size={17} /> Traitement administratif obligatoire
              </p>
              <p className="mt-2">
                MMJ peut supprimer ou anonymiser les donnees de compte lorsque c'est possible. Certaines donnees peuvent etre conservees si elles sont necessaires aux obligations legales, audits, transactions, bonus, securite ou prevention de fraude.
              </p>
            </div>

            {requestId ? (
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800">
                Demande recue. Reference: <strong>{requestId}</strong>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label>E-mail du compte</Label>
                  <Input type="email" value={form.email} onChange={(e) => update("email", e.target.value)} required className="mt-1" />
                </div>
                <div>
                  <Label>Nom complet</Label>
                  <Input value={form.full_name} onChange={(e) => update("full_name", e.target.value)} className="mt-1" />
                </div>
                <div>
                  <Label>Motif ou precision</Label>
                  <Textarea value={form.reason} onChange={(e) => update("reason", e.target.value)} rows={4} className="mt-1" />
                </div>
                <Button type="submit" disabled={submitting} className="w-full bg-red-700 text-white hover:bg-red-800">
                  {submitting ? "Envoi..." : "Envoyer la demande"}
                </Button>
              </form>
            )}

            <p className="text-xs leading-5 text-[#5C625F]">
              Cette page peut etre utilisee depuis le web ou depuis l'application Android pour satisfaire les exigences de suppression de compte Google Play.
            </p>
          </CardContent>
        </Card>

        <ContactInfoBlock />
      </div>
    </main>
  );
}
